/**
 * Croc Cutseal AI — Live affordance min-cut under Soft≠Live.
 * Soft edges carry 0 Live flow. Live CTA only if max-flow(source→sink) ≥ 1.
 * Twinplane tiers label nodes; Pathseal chain is obsolete as hero.
 * Soft local only · no firmware · no production claim.
 */
import { useMemo, useState } from "react";
import { html, stop } from "./ui.js";
import { path, useApi } from "./api.js";

export function isLocalHost() {
  try {
    const h = String(location.hostname || "");
    return h === "127.0.0.1" || h === "localhost" || h === "[::1]";
  } catch {
    return false;
  }
}

const TIER_RANK = { broken: 0, unknown: 1, soft: 2, live: 3 };

export function meetTier(a, b) {
  const r = Math.min(TIER_RANK[a] ?? 1, TIER_RANK[b] ?? 1);
  return r === 3 ? "live" : r === 2 ? "soft" : r === 0 ? "broken" : "unknown";
}

const INTENT_META = [
  {
    id: "evidence_snap",
    title: "Evidence snapshot",
    say: "Live flow must cross presence → silicon → bind → lanes → skill → receipt",
  },
  {
    id: "panic_mesh",
    title: "Panic mesh awaken",
    say: "Edge may local-act; Live cloud claim needs mesh receipt path with Live capacity",
  },
  {
    id: "ota_campaign",
    title: "OTA campaign seal",
    say: "Silicon family is a hard cut · cross-rev capacity_live = 0 forever",
  },
];

/** Ford–Fulkerson max-flow on tiny graphs (≤20 nodes). */
export function maxFlowMinCut(nodes, edges, source, sink) {
  const ids = nodes.map((n) => n.id);
  const cap = {};
  const adj = {};
  for (const id of ids) {
    adj[id] = [];
    cap[id] = {};
  }
  for (const e of edges) {
    if (!adj[e.from] || !adj[e.to]) continue;
    adj[e.from].push(e.to);
    adj[e.to].push(e.from);
    cap[e.from][e.to] = (cap[e.from][e.to] || 0) + (e.capacity || 0);
    cap[e.to][e.from] = cap[e.to][e.from] || 0;
  }

  function bfs(parent) {
    const seen = new Set([source]);
    const q = [source];
    while (q.length) {
      const u = q.shift();
      for (const v of adj[u] || []) {
        if (!seen.has(v) && (cap[u][v] || 0) > 0) {
          seen.add(v);
          parent[v] = u;
          if (v === sink) return true;
          q.push(v);
        }
      }
    }
    return false;
  }

  let flow = 0;
  const parent = {};
  while (bfs(parent)) {
    let bottleneck = Infinity;
    for (let v = sink; v !== source; v = parent[v]) {
      const u = parent[v];
      bottleneck = Math.min(bottleneck, cap[u][v]);
    }
    for (let v = sink; v !== source; v = parent[v]) {
      const u = parent[v];
      cap[u][v] -= bottleneck;
      cap[v][u] = (cap[v][u] || 0) + bottleneck;
    }
    flow += bottleneck;
  }

  // residual reachability from source → S side of min-cut
  const reach = new Set();
  const stack = [source];
  while (stack.length) {
    const u = stack.pop();
    if (reach.has(u)) continue;
    reach.add(u);
    for (const v of adj[u] || []) {
      if ((cap[u][v] || 0) > 0 && !reach.has(v)) stack.push(v);
    }
  }

  const cut = [];
  for (const e of edges) {
    if (reach.has(e.from) && !reach.has(e.to) && (e.capacity || 0) >= 0) {
      // original edge crosses cut if residual shows separation
      cut.push({ ...e, why: e.why || "zero Live capacity or saturated" });
    }
  }
  // Prefer reporting original zero-capacity edges that block required paths
  const zeroBlocks = edges.filter(
    (e) => (e.capacity || 0) === 0 && e.liveEdge,
  );
  const cutOut = flow > 0 ? cut : zeroBlocks.length ? zeroBlocks : cut;

  return { flow, cut: cutOut, residualReach: [...reach] };
}

function node(id, label, tier, plane, surface, reason) {
  return {
    id,
    label,
    tier: tier || "unknown",
    plane: plane || "edge",
    surface: surface || "",
    reason: reason || "",
  };
}

function liveCap(tier) {
  return tier === "live" ? 1 : 0;
}

/**
 * Build affordance graph + cutseal pack for an intent.
 */
export function buildCutseal(intentId, ctx) {
  const {
    device,
    offline,
    softLanes,
    forceAllLive,
    forceOtaCrossFamily,
  } = ctx || {};

  if (forceAllLive) {
    const nodes = [
      node("src", "Source", "live", "meta", "", "Soft·demo"),
      node("n1", "All facts", "live", "edge", "Ops", "Soft·demo"),
      node("sink", "Intent effect", "live", "meta", "", "Soft·demo"),
    ];
    const edges = [
      { from: "src", to: "n1", capacity: 1, liveEdge: true, why: "demo" },
      { from: "n1", to: "sink", capacity: 1, liveEdge: true, why: "demo" },
    ];
    const { flow, cut } = maxFlowMinCut(nodes, edges, "src", "sink");
    return pack(intentId, nodes, edges, flow, cut, "Soft·demo cutseal · not production", true);
  }

  const offlineSet = new Set(offline || []);
  const devId = device && (device.id || device);
  const present = !!devId && !offlineSet.has(String(devId));
  const kind = String((device && device.kind) || "").toLowerCase();
  const hw = String(
    (device && (device.board_profile || device.hw_code_resolved || device.product_hw_code || "")) || "",
  ).toLowerCase();
  const isP4 = /p4|esp32.?p4/.test(hw);
  const rev3 = /rev.?3|>=3|v3/.test(hw);
  const silFamily = isP4 ? (rev3 ? "p4_rev3" : "p4_rev1") : "generic";

  const lanes = (softLanes && softLanes.lanes) || softLanes || {};
  const laneMode = (name) => {
    const L = lanes[name] || {};
    if (L.live === true || String(L.mode || "").toLowerCase() === "live") return "live";
    if (L.soft === true || /soft|shadow/i.test(String(L.mode || ""))) return "soft";
    if (String(L.mode || "") === "off") return "broken";
    return isLocalHost() ? "soft" : "unknown";
  };
  const camLane = laneMode("camera") || laneMode("snapshot") || laneMode("vision");
  const ruleLane = laneMode("rules") || laneMode("oncr") || "soft";

  if (intentId === "ota_campaign") {
    const cross = forceOtaCrossFamily !== false;
    const silTier = cross ? "broken" : "live";
    const nodes = [
      node("src", "Source", "live", "meta"),
      node("presence", "Target present", present ? "live" : "soft", "edge", "Ops map", present ? String(devId) : "no target"),
      node("silicon", "Silicon family", silTier, "edge", "Firmware SoT", cross ? "P4 rev1.x ↛ rev3.x" : silFamily),
      node("token", "Serve token/host", "soft", "cloud", "OTA serve"),
      node("receipt", "Boot report", silTier === "broken" ? "broken" : "soft", "edge", "Device report"),
      node("sink", "OTA Live effect", "live", "meta"),
    ];
    const edges = linkChain(
      ["src", "presence", "silicon", "token", "receipt", "sink"],
      (id) => nodes.find((n) => n.id === id),
    );
    const { flow, cut } = maxFlowMinCut(nodes, edges, "src", "sink");
    return pack(intentId, nodes, edges, flow, cut, "OTA Cutseal · silicon is hard cut", false);
  }

  if (intentId === "panic_mesh") {
    const nodes = [
      node("src", "Source", "live", "meta"),
      node("signal", "Panic / DI", "soft", "edge", "Device signal"),
      node("presence", "Device present", present ? "live" : "broken", "edge", "Ops map", present ? String(devId) : "no focus"),
      node("mesh", "Mesh set", present ? "soft" : "broken", "edge", "Mesh layer"),
      node("policy", "Lifeline policy", "soft", "cloud", "Edge offline"),
      node("fanout", "Cloud fanout", "soft", "cloud", "Cmd path"),
      node("receipt", "Brother ack", "unknown", "edge", "Mesh ack"),
      node("sink", "Mesh Live claim", "live", "meta"),
    ];
    // Two routes: edge-local soft path cannot feed Live sink; cloud path soft
    const edges = [
      ...chainEdges(["src", "signal", "presence", "mesh", "receipt", "sink"], nodes),
      ...chainEdges(["src", "policy", "fanout", "sink"], nodes),
    ];
    const { flow, cut } = maxFlowMinCut(nodes, edges, "src", "sink");
    return pack(
      intentId,
      nodes,
      edges,
      flow,
      cut,
      "Panic Cutseal · Soft mesh never carries Live cloud claim",
      false,
      "Edge may local-alert Soft — Cloud must not claim Live done",
    );
  }

  // evidence_snap
  const bindOk = kind === "camera" || /cam|vision/.test(hw);
  const bindTier = bindOk ? (camLane === "live" ? "live" : "soft") : "broken";
  const laneTier = meetTier(camLane, ruleLane);
  const nodes = [
    node("src", "Source", "live", "meta"),
    node("presence", "Device present", present ? "live" : "broken", "edge", "Ops map", present ? String(devId) : "pick device"),
    node("silicon", "Board / silicon", present ? "live" : "unknown", "edge", "Fleet", silFamily),
    node("bind", "Camera bind", bindTier, "edge", "Activate", bindOk ? "candidate" : "not camera-class"),
    node("lanes", "soft-lanes", laneTier, "cloud", "soft-lanes", `cam=${camLane} rules=${ruleLane}`),
    node("skill", "Skill digest", "soft", "cloud", "Platform skills"),
    node("zone", "Zone scope", present ? "soft" : "unknown", "cloud", "Authz"),
    node("receipt", "Snapshot receipt", "unknown", "edge", "Camera delivery"),
    node("sink", "Evidence Live effect", "live", "meta"),
  ];
  const edges = chainEdges(
    ["src", "presence", "silicon", "bind", "lanes", "skill", "zone", "receipt", "sink"],
    nodes,
  );
  const { flow, cut } = maxFlowMinCut(nodes, edges, "src", "sink");
  return pack(
    intentId,
    nodes,
    edges,
    flow,
    cut,
    "Evidence Cutseal · Live only if flow≥1 through all Live-capacity edges",
    false,
  );
}

function chainEdges(ids, nodes) {
  const by = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const out = [];
  for (let i = 0; i < ids.length - 1; i++) {
    const a = by[ids[i]];
    const b = by[ids[i + 1]];
    if (!a || !b) continue;
    // capacity from node a: only live carries Live flow into next
    // sink always accepts if predecessor live
    const cap =
      a.id === "src"
        ? liveCap(b.tier === "broken" ? "broken" : a.tier)
        : b.id === "sink"
          ? liveCap(a.tier)
          : Math.min(liveCap(a.tier), b.tier === "broken" ? 0 : 1);
    out.push({
      from: a.id,
      to: b.id,
      capacity: cap,
      liveEdge: true,
      why:
        cap === 0
          ? a.id === "src"
            ? `${b.label} blocks Live (${b.tier})`
            : b.id === "sink"
              ? `${a.label} is ${a.tier} · cannot feed Live sink`
              : `${a.label} is ${a.tier} → 0 Live capacity`
          : `${a.label} → ${b.label}`,
    });
  }
  return out;
}

function linkChain(ids, get) {
  const nodes = ids.map(get).filter(Boolean);
  return chainEdges(
    ids,
    nodes,
  );
}

function pack(intentId, nodes, edges, flow, cut, note, demo, forbidden) {
  const facts = nodes.filter((n) => n.id !== "src" && n.id !== "sink");
  const cloudMeet = facts
    .filter((n) => n.plane === "cloud")
    .map((n) => n.tier)
    .reduce((a, b) => meetTier(a, b), "live");
  const edgeMeet = facts
    .filter((n) => n.plane === "edge")
    .map((n) => n.tier)
    .reduce((a, b) => meetTier(a, b), "live");
  const liveOk = flow >= 1 && !demo;
  const repairs = (cut || [])
    .map((e) => {
      const n = nodes.find((x) => x.id === e.from) || nodes.find((x) => x.id === e.to);
      return {
        id: e.from + "→" + e.to,
        label: n ? n.label : e.from,
        tier: n ? n.tier : "soft",
        why: e.why,
        surface: n ? n.surface : "",
      };
    })
    .filter((r, i, arr) => arr.findIndex((x) => x.id === r.id) === i)
    .slice(0, 6);

  return {
    intentId,
    nodes,
    edges,
    flow,
    cut: cut || [],
    repairs,
    meet: flow >= 1 ? "live" : repairs.some((r) => r.tier === "broken") ? "broken" : "soft",
    cloudMeet: cloudMeet || "unknown",
    edgeMeet: edgeMeet || "unknown",
    note,
    demo: !!demo,
    liveOk: liveOk || (!!demo && flow >= 1),
    forbidden:
      forbidden ||
      (flow < 1
        ? "Forbidden Live claim: would assert action complete — cut open"
        : "No forbidden claim — Live path capacity exists (verify routes)"),
  };
}

/** Compat shims */
export function buildTwinplane(intentId, ctx) {
  return buildCutseal(intentId, ctx);
}
export function buildPathseal(intentId, ctx) {
  return buildCutseal(intentId, ctx);
}

/** Live hash for Cutseal repair surface labels (Soft brochure URLs forbidden). */
function goSurface(surface) {
  try {
    if (!surface) return;
    const s = String(surface).toLowerCase();
    if (s.includes("camera delivery") || s.includes("delivery")) {
      location.hash = "#/cameras/live";
      return;
    }
    if (s.includes("activate") || s.includes("camera")) {
      location.hash = "#/activate";
      return;
    }
    if (s.includes("ota") || s.includes("firmware") || s.includes("fleet")) {
      location.hash = "#/fleet/firmware";
      return;
    }
    if (s.includes("soft-lane") || s.includes("skill") || s.includes("platform")) {
      location.hash = "#/settings/ai";
      return;
    }
    if (s.includes("authz") || s.includes("zone") || s.includes("mesh") || s.includes("ops")) {
      location.hash = "#/ops";
      return;
    }
    if (s.includes("cmd") || s.includes("edge offline")) {
      location.hash = "#/edge";
      return;
    }
  } catch {
    /* ignore */
  }
}

/**
 * Soft≠Live Page41 — open the real Live desk for an intent.
 * Soft “would call Live” toast invent is deleted on production.
 */
export function openLiveIntent(intentId, { device, onSelect, toast, local } = {}) {
  const id = String(intentId || "");
  if (local) {
    toast && toast("Soft·demo Cutseal · no Live hardware act · Page41", "warn");
    return "soft_demo";
  }
  if (id === "evidence_snap") {
    if (device && onSelect) onSelect(device.id || device);
    location.hash = "#/cameras/live";
    toast && toast("Live cameras path · Soft evidence invent deleted", "go");
    return "live_cameras";
  }
  if (id === "panic_mesh") {
    if (device && onSelect) onSelect(device.id || device);
    location.hash = "#/ops";
    toast && toast("Live Ops map · mesh Live needs device/mesh receipt", "go");
    return "live_ops";
  }
  if (id === "ota_campaign") {
    location.hash = "#/fleet/firmware";
    toast && toast("Live OTA desk · Soft campaign invent deleted", "go");
    return "live_ota";
  }
  location.hash = "#/ops";
  toast && toast("Opened Live Ops for this intent", "go");
  return "live_ops";
}

export function PathsealPanel({ device, offline, onSelect, toast }) {
  return html`<${CutsealPanel} device=${device} offline=${offline} onSelect=${onSelect} toast=${toast}/>`;
}
export function TwinplanePanel({ device, offline, onSelect, toast }) {
  return html`<${CutsealPanel} device=${device} offline=${offline} onSelect=${onSelect} toast=${toast}/>`;
}

export function CutsealPanel({ device, offline, onSelect, toast }) {
  const local = isLocalHost();
  const lanesQ = useApi(path("get__ai_soft-lanes"));
  const softLanes = (lanesQ && lanesQ.data) || null;
  const [intentId, setIntentId] = useState("evidence_snap");
  const [forceAll, setForceAll] = useState(false);
  const [crossOta, setCrossOta] = useState(true);

  const pack = useMemo(
    () =>
      buildCutseal(intentId, {
        device,
        offline,
        softLanes,
        forceAllLive: local && forceAll,
        forceOtaCrossFamily: crossOta,
      }),
    [intentId, device, offline, softLanes, forceAll, crossOta, local],
  );

  const flowLabel =
    pack.flow >= 1 ? "Live flow ≥ 1"
      : pack.meet === "broken" ? "Live flow 0 · hard cut"
      : "Live flow 0 · Soft only";

  const graphNodes = (pack.nodes || []).filter((n) => n.id !== "src");

  const canLive = !!(pack.liveOk || (local && forceAll && pack.flow >= 1));
  const firstRepair = (pack.repairs && pack.repairs[0]) || null;

  return html`
    <section className="cutseal" data-cmc-cutseal="page41" data-meet=${pack.meet} aria-label="Cutseal AI Page41">
      <div className="cutseal__head">
        <div>
          <p className="cutseal__eyebrow">Cutseal AI · Live intent</p>
          <h3>Live unlock only when capacity exists.</h3>
          <p className="cutseal__lede">
            Soft edges carry zero Live flow. Max-flow source→effect must be ≥1 before Live desks open.
            Min-cut is the repair ladder — not Soft theatre.${local ? " · Soft sandpit on 127 only." : ""}
          </p>
        </div>
        <span className="cutseal__seal" data-meet=${pack.meet}>${flowLabel}</span>
      </div>

      <div className="cutseal__intents" role="tablist">
        ${INTENT_META.map((m) => html`
          <button key=${m.id} type="button" role="tab"
            className="cutseal__intent"
            data-on=${intentId === m.id ? "1" : "0"}
            onClick=${(e) => { stop(e); setIntentId(m.id); setForceAll(false); }}>
            ${m.title}</button>`)}
      </div>
      <p className="cutseal__say">${(INTENT_META.find((m) => m.id === intentId) || {}).say || ""}</p>

      <div className="cutseal__flowstrip" aria-label="Affordance chain">
        ${graphNodes.map((n, i) => html`
          <span key=${n.id} className="cutseal__node" data-tier=${n.tier} data-plane=${n.plane}>
            <b>${n.label}</b>
            <em>${n.tier}</em>
          </span>
          ${i < graphNodes.length - 1 ? html`<i className="cutseal__arrow" aria-hidden="true"/>` : null}`)}
      </div>

      <div className="cutseal__metrics">
        <span>max-flow Live <b data-ok=${pack.flow >= 1 ? "1" : "0"}>${pack.flow}</b></span>
        <span>cloud tags <b data-tier=${pack.cloudMeet}>${pack.cloudMeet}</b></span>
        <span>edge tags <b data-tier=${pack.edgeMeet}>${pack.edgeMeet}</b></span>
        <span>meet <b data-tier=${pack.meet}>${pack.meet}</b></span>
      </div>

      <div className="cutseal__cut">
        <header>Min-cut · repair ladder</header>
        ${pack.repairs && pack.repairs.length
          ? pack.repairs.map((r) => html`
            <button key=${r.id} type="button" className="cutseal__chip" data-tier=${r.tier}
              onClick=${(e) => { stop(e); goSurface(r.surface); }}>
              <b>${r.label}</b>
              <em>${r.tier}</em>
              <span>${r.why}</span>
              ${r.surface ? html`<small>${r.surface}</small>` : null}
            </button>`)
          : html`<p className="cutseal__none">No Live cut — path capacity open (still Soft until Live routes).</p>`}
      </div>

      <p className="cutseal__forbid" data-bad=${pack.flow < 1 ? "1" : "0"}>${pack.forbidden}</p>
      <p className="cutseal__note">${pack.note}</p>

      <div className="cutseal__acts">
        <button type="button"
          className=${"btn btn--sm" + (canLive ? " btn--go" : "")}
          disabled=${!canLive}
          onClick=${(e) => {
            stop(e);
            openLiveIntent(intentId, { device, onSelect, toast, local: local && forceAll });
          }}>
          ${canLive
            ? (local && forceAll ? "Soft·demo flow open" : "Open Live path · Live")
            : "Live path locked"}</button>
        ${firstRepair && pack.flow < 1 ? html`
          <button type="button" className="btn btn--sm btn--ghost"
            onClick=${(e) => {
              stop(e);
              goSurface(firstRepair.surface);
              toast && toast("Opened Live repair surface", "go");
            }}>
            Open min-cut repair · Live</button>` : null}
        <button type="button" className="btn btn--sm btn--ghost"
          disabled=${!device}
          onClick=${(e) => {
            stop(e);
            if (device && onSelect) onSelect(device.id || device);
          }}>
          Focus device · Live</button>
        ${local ? html`
          <button type="button" className="btn btn--sm btn--ghost"
            onClick=${(e) => { stop(e); setForceAll((v) => !v); }}>
            ${forceAll ? "Clear Soft sandpit" : "Soft·demo open flow"}</button>
          ${intentId === "ota_campaign" ? html`
            <button type="button" className="btn btn--sm btn--ghost"
              onClick=${(e) => { stop(e); setCrossOta((v) => !v); }}>
              ${crossOta ? "Show matched rev" : "Show cross-rev cut"}</button>` : null}` : null}
      </div>
    </section>`;
}
